import { useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';
import LegacyRedirect from '@/components/LegacyRedirect';
import BrandFaviconManager from '@/components/brand/BrandFaviconManager';
import InstallMetaManager from '@/components/venue/InstallMetaManager';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import { BookingConfigProvider } from '@/lib/BookingConfigContext';
import AppLayout from '@/components/layout/AppLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import Home from '@/pages/Home';
import Shop from '@/pages/Shop';
import ProductDetail from '@/pages/ProductDetail';
import Simulators from '@/pages/Simulators';
import SimulatorDetail from '@/pages/SimulatorDetail';
import Bookings from '@/pages/Bookings.jsx';
import Corporate from '@/pages/Corporate';
import Parties from '@/pages/Parties';
import PartyBalance from '@/pages/PartyBalance';
import Contact from '@/pages/Contact';
import About from '@/pages/About';
import Blog from '@/pages/Blog';
import BlogArticle from '@/pages/BlogArticle';
import Orders from '@/pages/Orders';
import Account from '@/pages/Account';
// Admin invitation set-up flow — linked from the invite email
import AcceptInvite from '@/pages/AcceptInvite';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminCrm from '@/pages/AdminCrm';
import AdminCaseWorkspace from '@/pages/AdminCaseWorkspace';
import AdminNewsletter from '@/pages/AdminNewsletter';
import AdminUsers from '@/pages/AdminUsers';
import AdminSmsInbox from '@/pages/AdminSmsInbox';
import AdminEmailInbox from '@/pages/AdminEmailInbox';
import AdminCategories from '@/pages/AdminCategories';
import AdminReviews from '@/pages/AdminReviews';
import AdminProducts from '@/pages/AdminProducts';
import AdminStockControl from '@/pages/AdminStockControl';
import AdminSocialPosts from '@/pages/AdminSocialPosts';
import AdminShowcaseRigs from '@/pages/AdminShowcaseRigs';
import AdminPageMedia from '@/pages/AdminPageMedia';
import AdminMarketing from '@/pages/AdminMarketing';
import AdminOrders from '@/pages/AdminOrders';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminBrandAssets from '@/pages/AdminBrandAssets';
import AdminMaintenance from '@/pages/AdminMaintenance';
import AdminCompensation from '@/pages/AdminCompensation';
import AdminCorporateBrochure from '@/pages/AdminCorporateBrochure';
import AdminGroupBookings from '@/pages/AdminGroupBookings';
import AdminBookings from '@/pages/AdminBookings';
import AdminBlog from '@/pages/AdminBlog';
import AdminExperiences from '@/pages/AdminExperiences';
import AdminRigs from '@/pages/AdminRigs';
import AdminLiveTelemetry from '@/pages/AdminLiveTelemetry';
import AdminReturns from '@/pages/AdminReturns';
import Returns from '@/pages/Returns';
import GiftCards from '@/pages/GiftCards';
import AdminGiftCards from '@/pages/AdminGiftCards';
import AdminDiscountCodes from '@/pages/AdminDiscountCodes';
import AdminShipping from '@/pages/AdminShipping';
import AdminSessionResults from '@/pages/AdminSessionResults';
import AdminVenue from '@/pages/AdminVenue';
import AdminOpeningHours from '@/pages/AdminOpeningHours';
import AdminOrderNotifications from '@/pages/AdminOrderNotifications';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import RefundPolicy from '@/pages/RefundPolicy';
import DeliveryInfo from '@/pages/DeliveryInfo';
import TermsConditions from '@/pages/TermsConditions';
import LeaveReview from '@/pages/LeaveReview';
import Feedback from '@/pages/Feedback';
import CaseFeedback from '@/pages/CaseFeedback';
import CustomerService from '@/pages/CustomerService';
import Faq from '@/pages/Faq';
import B2BPortal from '@/pages/B2BPortal';
import B2BApply from '@/pages/B2BApply';
import Competition from '@/pages/Competition';
import AdminCompetition from '@/pages/AdminCompetition';
import AdminInvoiceDesign from '@/pages/AdminInvoiceDesign';
import AdminInvoices from '@/pages/AdminInvoices';
import AdminFeedback from '@/pages/AdminFeedback';
import AdminEmailTemplates from '@/pages/AdminEmailTemplates';
import AdminEmailTesting from '@/pages/AdminEmailTesting';
import AdminRedirects from '@/pages/AdminRedirects';
import AdminLocalSync from '@/pages/AdminLocalSync';
import AdminApplePay from '@/pages/AdminApplePay';
import AdminCustomerService from '@/pages/AdminCustomerService';
import AdminEbay from '@/pages/AdminEbay';
import AdminB2B from '@/pages/AdminB2B';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import LiveTelemetry from '@/pages/LiveTelemetry';
import Leaderboard from '@/pages/Leaderboard';
import RigDetail from '@/pages/RigDetail';
import Twitch from '@/pages/Twitch';
import WalkIn from '@/pages/WalkIn';
import VenueKiosk from '@/pages/VenueKiosk';
import VenueLeaderboard from '@/pages/venue/VenueLeaderboard';
import VenueLive from '@/pages/venue/VenueLive';
import PosRedirect from '@/components/PosRedirect';
import { CartProvider } from '@/lib/CartContext';

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <>
    <RouteTracker />
    <ScrollToTop />
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Home />} />
        {/* Legacy /home URL — redirect to the canonical homepage */}
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/simulators" element={<Simulators />} />
        <Route path="/simulators/:slug" element={<SimulatorDetail />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/:id" element={<ProductDetail />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/corporate" element={<Corporate />} />
        <Route path="/parties" element={<Parties />} />
        <Route path="/party-balance/:token" element={<PartyBalance />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/customer-service" element={<CustomerService />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/about" element={<About />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/account" element={<Account />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/live" element={<LiveTelemetry />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/live/:rigKey" element={<RigDetail />} />
        <Route path="/twitch" element={<Twitch />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/delivery" element={<DeliveryInfo />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/review" element={<LeaveReview />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/competition" element={<Competition />} />
        <Route path="/b2b" element={<B2BPortal />} />
        <Route path="/b2b/apply" element={<B2BApply />} />
        <Route path="/b2b-portal" element={<Navigate to="/b2b" replace />} />
      </Route>

      <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/invoices" element={<AdminInvoices />} />
        {/* Customer CRM holds the cases queue and the customer directory as
            tabs: /admin/crm is the canonical cases queue and /admin/crm/directory
            the directory. The old directory and case-queue addresses redirect in. */}
        <Route path="/admin/customers" element={<Navigate to="/admin/crm/directory" replace />} />
        <Route path="/admin/crm" element={<AdminCrm />} />
        <Route path="/admin/crm/directory" element={<AdminCrm />} />
        <Route path="/admin/crm/cases" element={<Navigate to="/admin/crm" replace />} />
        <Route path="/admin/crm/cases/:id" element={<AdminCaseWorkspace />} />
        <Route path="/admin/returns" element={<AdminReturns />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/stock-control" element={<AdminStockControl />} />
        <Route path="/admin/categories" element={<AdminCategories />} />
        <Route path="/admin/discount-codes" element={<AdminDiscountCodes />} />
        <Route path="/admin/shipping" element={<AdminShipping />} />
        <Route path="/admin/reviews" element={<AdminReviews />} />
        <Route path="/admin/competition" element={<AdminCompetition />} />
        <Route path="/admin/invoice-design" element={<AdminInvoiceDesign />} />
        <Route path="/admin/gift-cards" element={<AdminGiftCards />} />
        <Route path="/admin/bookings" element={<AdminBookings />} />
        <Route path="/admin/group-bookings" element={<AdminGroupBookings />} />
        <Route path="/admin/experiences" element={<AdminExperiences />} />
        <Route path="/admin/rigs" element={<AdminRigs />} />
        <Route path="/admin/showcase-rigs" element={<AdminShowcaseRigs />} />
        <Route path="/admin/live-telemetry" element={<AdminLiveTelemetry />} />
        <Route path="/admin/session-results" element={<AdminSessionResults />} />
        <Route path="/admin/feedback" element={<AdminFeedback />} />
        <Route path="/admin/venue" element={<AdminVenue />} />
        <Route path="/admin/opening-hours" element={<AdminOpeningHours />} />
        <Route path="/admin/marketing" element={<AdminMarketing />} />
        <Route path="/admin/social-posts" element={<AdminSocialPosts />} />
        <Route path="/admin/newsletter" element={<AdminNewsletter />} />
        <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
        <Route path="/admin/email-testing" element={<AdminEmailTesting />} />
        <Route path="/admin/order-notifications" element={<AdminOrderNotifications />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/page-media" element={<AdminPageMedia />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/sms-inbox" element={<AdminSmsInbox />} />
        <Route path="/admin/email-inbox" element={<AdminEmailInbox />} />
        <Route path="/admin/ebay" element={<AdminEbay />} />
        <Route path="/admin/b2b" element={<AdminB2B />} />
        <Route path="/admin/customer-service" element={<AdminCustomerService />} />
        <Route path="/admin/redirects" element={<AdminRedirects />} />
        <Route path="/admin/local-sync" element={<AdminLocalSync />} />
        <Route path="/admin/apple-pay" element={<AdminApplePay />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/brand-assets" element={<AdminBrandAssets />} />
        <Route path="/admin/maintenance" element={<AdminMaintenance />} />
        <Route path="/admin/compensation" element={<AdminCompensation />} />
        <Route path="/admin/corporate-brochure" element={<AdminCorporateBrochure />} />
      </Route>
      {/* Venue tablet POS — admin-only, same venue toolset as the kiosk */}
      <Route path="/venue/pos" element={<BookingConfigProvider><WalkIn /></BookingConfigProvider>} />
      {/* Legacy /pos address — permanent redirect, query string preserved */}
      <Route path="/pos" element={<PosRedirect />} />
      {/* Venue tablet kiosk — admin-only check-in and rig dispatch, no admin chrome */}
      <Route path="/venue/kiosk" element={<VenueKiosk />} />
      {/* Venue wall displays — public, chrome-free, no layout so nothing but live data renders */}
      <Route path="/venue/leaderboard" element={<VenueLeaderboard />} />
      <Route path="/venue/live" element={<VenueLive />} />
      {/* Public resolution-feedback landing page — no layout, no login */}
      <Route path="/feedback/:referenceCode" element={<CaseFeedback />} />
      <Route path="*" element={<LegacyRedirect />} />
    </Routes>
    </>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <CartProvider>
            <BrandFaviconManager />
            <InstallMetaManager />
            <AuthenticatedApp />
          </CartProvider>
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App